export const translations = {
  en: {
    nav: {
      home: 'Home',
      products: 'Products',
      projectAct: 'Project ACT\u2122',
      story: 'Our Story',
      healthcare: 'Healthcare',
      contact: 'Contact',
      shopEtsy: 'Shop on Etsy'
    },
    hero: {
      title: 'Protect What Protects You',
      subtitle: 'Protective cases designed to help carry, organize, and identify emergency medication and compatible supplies in real life.',
      cta: 'Shop Emergency Cases',
      ctaSecondary: 'Explore the Designs'
    },
    stats: {
      note: 'Verified activity from the MyGuardPod Etsy shop.'
    },
    marquee: 'EpiPen \u2022 Nitroglycerin \u2022 Naloxone \u2022 Valtoco \u2022 Nayzilam \u2022 Emergency Hydrocortisone \u2022 Midazolam \u2022 Seizure Rescue \u2022 Anaphylaxis',
    features: {
      title: 'Protection Built for Everyday Carry',
      subtitle: 'Purpose-built protective carrying cases for compatible emergency-medication containers and supplies.',
      impact: {
        title: 'Physical Protection for Everyday Carry',
        description: 'A rigid outer shell and fitted flexible insert help limit movement and add a physical barrier against routine knocks, pressure and wear during transport. Protection varies by model, configuration and conditions of use.'
      },
      temperature: {
        title: 'Follow the Storage Instructions',
        description: 'Standard MyGuardPod cases do not control temperature. Avoid vehicles, direct sunlight, freezing conditions and excessive heat. Always follow the medication manufacturer\u2019s storage instructions.'
      },
      access: {
        title: 'Visible, Organized and Accessible',
        description: 'High-visibility identification and format-specific layouts help keep compatible medication containers and supplies organized and easier to locate. The case does not replace an emergency plan or training.'
      },
      compatible: 'Purpose-built formats for compatible containers of medications such as Valtoco, Nayzilam, emergency hydrocortisone injection kits, EpiPen and Naloxone. Compatibility depends on the specific format, dose and packaging listed in each case.'
    },
    products: {
      title: 'Protective Cases for Every Emergency',
      subtitle: 'Find the protective case designed specifically for your essential emergency medication.',
      viewAll: 'Browse All Protective Cases',
      categories: {
        epilepsy: {
          title: 'Seizure Rescue Medication Cases',
          products: 'Valtoco \u00b7 Nayzilam \u00b7 Midazolam'
        },
        adrenal: {
          title: 'Adrenal Emergency Kit Cases',
          products: 'CortoPod ACT\u2122 lineup \u2014 emergency hydrocortisone injection kits'
        },
        allergy: {
          title: 'Anaphylaxis & Allergy Cases',
          products: 'EpiPen \u00b7 Auvi-Q \u00b7 Generic Epinephrine'
        },
        heart: {
          title: 'Cardiac Emergency Cases',
          products: 'Nitroglycerin Tablets & Spray'
        },
        overdose: {
          title: 'Opioid Overdose Reversal Cases',
          products: 'Naloxone \u00b7 Narcan'
        },
        asthma: {
          title: 'Asthma Inhaler Cases',
          products: 'Protective case for asthma inhalers'
        }
      }
    },
    quebec: {
      title: 'Designed & Manufactured in Qu\u00e9bec, Canada',
      description: 'Every MyGuardPod case is designed, 3D-printed, and assembled by hand in Baie-Comeau, Qu\u00e9bec. No outsourcing, no mass production \u2014 purpose-built carrying cases produced in small batches.',
      features: ['Small-batch production', 'Made to order in Qu\u00e9bec', 'Rapid design iteration', 'Real patient feedback']
    },
    whoFor: {
      title: "Who It's For",
      subtitle: 'If someone you care about depends on emergency medication, MyGuardPod was made for you.',
      patients: {
        title: 'Patients With Chronic Conditions',
        description: 'Carry, organize and identify your emergency medication every day \u2014 wherever life takes you.'
      },
      parents: {
        title: 'Parents of Children With Epilepsy',
        description: 'Keep your child\u2019s seizure medication organized and easier to identify at school, at home, or on the go.'
      },
      caregivers: {
        title: 'Caregivers & Family Members',
        description: 'Protected, organized and easier to identify \u2014 for the medication you help carry every day.'
      },
      healthcare: {
        title: 'Healthcare Professionals',
        description: 'Recommend a real-world, field-informed design for patients who need to carry emergency medication daily.'
      },
      responders: {
        title: 'First Responders',
        description: 'A carrying case designed to make the kit easier to identify and access.'
      },
      partnership: 'Schools, clinics, or organizations?',
      learnMore: 'Learn about partnerships'
    },
    origin: {
      badge: 'Our Origin',
      title: 'Born From a Real Emergency',
      description: "MyGuardPod started with a simple problem.\n\nEmergency-medication containers are often carried loose in backpacks, purses or pockets, where they can be knocked around, crushed or misplaced.\n\nWe design MyGuardPod carrying cases to add a physical barrier during everyday carry and to keep compatible medication containers organized, visible and easier to identify. Every case is 3D-printed by hand in Baie-Comeau, Qu\u00e9bec \u2014 purpose-built for a specific medication format and used by patients and caregivers around the world.",
      cta: 'Read our full story'
    },
    testimonial: {
      quote: '\u201cThis case is exactly what I needed. Solid construction, easy to spot in my bag, and I can access my medication in seconds. The peace of mind is priceless.\u201d',
      author: 'Parent of a child with epilepsy \u2014 United States'
    },
    faq: {
      title: 'Frequently Asked Questions',
      sections: [
        {
          title: 'General Questions',
          items: [
            {
              question: 'What is MyGuardPod?',
              answer: 'MyGuardPod designs protective carrying cases for essential emergency medications such as Valtoco, Nayzilam, emergency hydrocortisone injection kits, EpiPen, Naloxone and nitroglycerin. The cases are designed as protective carrying accessories only.'
            },
            {
              question: 'Why not just use a regular pouch?',
              answer: 'Emergency medications are fragile and often expensive. A regular pouch provides little protection against impacts, crushing, or environmental exposure. MyGuardPod cases are designed to protect medication in real-world situations like backpacks, pockets, travel bags or outdoor activities.'
            },
            {
              question: 'Where are MyGuardPod products made?',
              answer: 'All MyGuardPod cases are designed and produced in Qu\u00e9bec, Canada. Each product is manufactured in small batches using high-strength 3D printed materials and assembled by hand.'
            }
          ]
        },
        {
          title: 'Product Compatibility',
          items: [
            {
              question: 'How do I know if a case fits my medication?',
              answer: 'Each MyGuardPod case is designed for a specific medication or emergency kit. Product pages clearly list compatible medications to ensure a proper fit.'
            },
            {
              question: 'Can the cases hold other medications?',
              answer: 'Cases are optimized for specific medications. Some may fit similar items, but for the best protection we recommend using the case designed for your medication.'
            }
          ]
        },
        {
          title: 'Protection & Use',
          items: [
            {
              question: 'Do the cases protect against drops?',
              answer: 'MyGuardPod cases add a physical barrier against some everyday impacts, pressure, scratches, and normal movement inside a bag. They do not make medication or its container unbreakable and do not guarantee integrity following a significant drop or impact. After an incident, always inspect the medication, container, and packaging according to the official instructions.'
            },
            {
              question: 'Do the cases control temperature?',
              answer: 'No. Standard MyGuardPod cases do not actively heat, cool or control temperature. They do not extend shelf life or guarantee medication stability or effectiveness. Follow the medication manufacturer\u2019s storage instructions and consult a pharmacist or healthcare professional when needed.'
            },
            {
              question: 'Are the cases waterproof?',
              answer: 'No. Unless a specific product has a separately documented rating, MyGuardPod cases are not waterproof, watertight or submersible. Keep the case dry and inspect the medication after moisture exposure.'
            },
            {
              question: 'How do I confirm compatibility with my medication?',
              answer: 'Verify the medication name, dose, manufacturer, packaging type and dimensions listed in each case description. A similar appearance does not guarantee compatibility. Manufacturers may also update their packaging over time. When in doubt, contact us before purchasing.'
            },
            {
              question: 'Are the cases sterile or child-resistant?',
              answer: 'No. MyGuardPod cases are neither sterile nor child-resistant. They are protective carrying accessories only. Store your medication safely, out of reach of children, and follow the storage instructions provided with your medication.'
            }
          ]
        },
        {
          title: 'Orders & Shipping',
          items: [
            {
              question: 'Where do you ship from?',
              answer: 'Orders ship from Qu\u00e9bec, Canada.'
            },
            {
              question: 'Do you ship worldwide?',
              answer: 'Yes. MyGuardPod ships to many countries worldwide through Etsy.'
            },
            {
              question: 'How long does shipping take?',
              answer: 'Shipping times vary by destination but most orders are prepared and shipped within a few days.'
            },
            {
              question: 'Where can I buy MyGuardPod products?',
              answer: 'Products are currently available through the official MyGuardPod Etsy store.'
            },
            {
              question: 'I am in the U.S. Will I have to pay an additional tariff when my order is delivered?',
              answer: 'No. Our U.S. Etsy prices have already been adjusted to account for the current additional U.S. import tariff on affected Canadian-made products. You will not be asked to pay an additional 50% tariff upon delivery. See the \u201cImportant Information for U.S. Customers\u201d section below for details.'
            }
          ]
        },
        {
          title: 'Safety & Disclaimer',
          items: [
            {
              question: 'What is the intended function of a MyGuardPod case?',
              answer: 'MyGuardPod cases are sold as protective carrying accessories. They do not administer, diagnose, treat, dose, monitor or modify medication. MyGuardPod does not claim medical-device approval, authorization or certification.'
            },
            {
              question: 'Can I request a case for a specific medication?',
              answer: 'Yes. We regularly develop new designs based on customer needs. If you need a case for a specific medication, you can contact us to request a new design.'
            }
          ]
        }
      ]
    },
    contact: {
      title: 'Get in Touch',
      subtitle: 'Have questions about our products or interested in bulk orders? We\u2019d love to hear from you.',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        subject: 'Subject',
        message: 'Your Message',
        send: 'Send Message',
        sending: 'Sending...',
        success: 'Message sent successfully!',
        error: 'Failed to send message. Please try again.'
      },
      info: {
        location: 'Baie-Comeau, Qu\u00e9bec, Canada',
        email: 'contact@myguardpod.com'
      }
    },
    cta: {
      title: 'Ready to Carry, Organize and Identify Your Medication?',
      description: 'Browse the full collection on Etsy. Each case is made to order in Qu\u00e9bec. Current processing times are shown on the corresponding Etsy listing.',
      button: 'Shop on Etsy'
    },
    footer: {
      tagline: 'Protect What Protects You',
      description: 'Purpose-built protective carrying cases designed to organize, identify and add physical protection for compatible emergency-medication containers and supplies. Made to order in Qu\u00e9bec, Canada.',
      quickLinks: 'Quick Links',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      copyright: '\u00a9 2026 MyGuardPod. All rights reserved.',
      madeIn: 'Designed and manufactured in Baie-Comeau, Qu\u00e9bec'
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      products: 'Produits',
      projectAct: 'Projet ACT\u2122',
      story: 'Notre Histoire',
      healthcare: 'Sant\u00e9',
      contact: 'Contact',
      shopEtsy: 'Acheter sur Etsy'
    },
    hero: {
      title: 'Prot\u00e9gez Ce Qui Vous Prot\u00e8ge',
      subtitle: 'Des \u00e9tuis protecteurs con\u00e7us pour aider \u00e0 transporter, organiser et identifier les m\u00e9dicaments d\u2019urgence et leurs fournitures compatibles dans la vraie vie.',
      cta: 'Acheter sur Etsy',
      ctaSecondary: 'D\u00e9couvrir les mod\u00e8les'
    },
    stats: {
      note: 'Activit\u00e9 v\u00e9rifi\u00e9e depuis la boutique Etsy MyGuardPod.'
    },
    marquee: 'EpiPen \u2022 Nitroglyc\u00e9rine \u2022 Naloxone \u2022 Valtoco \u2022 Nayzilam \u2022 Hydrocortisone d\u2019urgence \u2022 Midazolam \u2022 Crise \u00e9pileptique \u2022 Anaphylaxie',
    features: {
      title: 'Protection con\u00e7ue pour le transport quotidien',
      subtitle: '\u00c9tuis de transport protecteurs con\u00e7us pour des contenants de m\u00e9dicaments d\u2019urgence et du mat\u00e9riel compatibles.',
      impact: {
        title: 'Protection physique pour le transport quotidien',
        description: 'Une coque ext\u00e9rieure rigide et un insert flexible ajust\u00e9 contribuent \u00e0 limiter les mouvements et ajoutent une barri\u00e8re physique contre les chocs courants, la pression et l\u2019usure pendant le transport. La protection varie selon le mod\u00e8le, la configuration et les conditions d\u2019utilisation.'
      },
      temperature: {
        title: 'Respectez les consignes de conservation',
        description: 'Les \u00e9tuis MyGuardPod standards ne contr\u00f4lent pas la temp\u00e9rature. \u00c9vitez les v\u00e9hicules, le soleil direct, le gel et la chaleur excessive. Respectez toujours les consignes de conservation du fabricant du m\u00e9dicament.'
      },
      access: {
        title: 'Visible, organis\u00e9 et accessible',
        description: 'L\u2019identification tr\u00e8s visible et les am\u00e9nagements adapt\u00e9s \u00e0 chaque format facilitent l\u2019organisation et le rep\u00e9rage des contenants de m\u00e9dicaments et du mat\u00e9riel compatibles. L\u2019\u00e9tui ne remplace pas un plan d\u2019urgence ni une formation.'
      },
      compatible: 'Formats con\u00e7us pour des contenants compatibles de m\u00e9dicaments comme Valtoco, Nayzilam, les kits d\u2019hydrocortisone injectable d\u2019urgence, EpiPen et Naloxone. La compatibilit\u00e9 d\u00e9pend du format, de la dose et de l\u2019emballage indiqu\u00e9s dans chaque fiche.'
    },
    products: {
      title: '\u00c9tuis protecteurs pour chaque urgence',
      subtitle: 'Trouvez l\u2019\u00e9tui protecteur con\u00e7u sp\u00e9cifiquement pour votre m\u00e9dicament essentiel.',
      viewAll: 'Voir tous les \u00e9tuis protecteurs',
      categories: {
        epilepsy: {
          title: '\u00c9tuis pour m\u00e9dicaments anti-crises',
          products: 'Valtoco \u00b7 Nayzilam \u00b7 Midazolam'
        },
        adrenal: {
          title: '\u00c9tuis pour kits d\u2019urgence surr\u00e9nalienne',
          products: 'Gamme CortoPod ACT\u2122 \u2014 kits d\u2019hydrocortisone injectable d\u2019urgence'
        },
        allergy: {
          title: '\u00c9tuis anaphylaxie et allergies',
          products: 'EpiPen \u00b7 Auvi-Q \u00b7 \u00c9pin\u00e9phrine g\u00e9n\u00e9rique'
        },
        heart: {
          title: '\u00c9tuis urgences cardiaques',
          products: 'Comprim\u00e9s et vaporisateur de nitroglyc\u00e9rine'
        },
        overdose: {
          title: '\u00c9tuis renversement de surdose opio\u00efdes',
          products: 'Naloxone \u00b7 Narcan'
        },
        asthma: {
          title: '\u00c9tuis pour inhalateurs d\u2019asthme',
          products: '\u00c9tui protecteur pour inhalateurs d\u2019asthme'
        }
      }
    },
    quebec: {
      title: 'Con\u00e7u et fabriqu\u00e9 au Qu\u00e9bec, Canada',
      description: 'Chaque \u00e9tui MyGuardPod est con\u00e7u, imprim\u00e9 en 3D et assembl\u00e9 \u00e0 la main \u00e0 Baie-Comeau, au Qu\u00e9bec. Pas de sous-traitance, pas de production de masse \u2014 \u00e9tuis de transport con\u00e7us sur mesure, produits en petits lots.',
      features: ['Production en petits lots', 'Fabriqu\u00e9 sur commande au Qu\u00e9bec', 'It\u00e9ration rapide du design', 'Retours de vrais patients']
    },
    whoFor: {
      title: 'Pour qui?',
      subtitle: 'Si quelqu\u2019un que vous aimez d\u00e9pend d\u2019un m\u00e9dicament d\u2019urgence, MyGuardPod a \u00e9t\u00e9 fait pour vous.',
      patients: {
        title: 'Patients avec conditions chroniques',
        description: 'Transportez, organisez et identifiez votre m\u00e9dicament d\u2019urgence chaque jour \u2014 o\u00f9 que la vie vous m\u00e8ne.'
      },
      parents: {
        title: 'Parents d\u2019enfants \u00e9pileptiques',
        description: 'Gardez le m\u00e9dicament anti-crise de votre enfant organis\u00e9 et plus facile \u00e0 identifier \u00e0 l\u2019\u00e9cole, \u00e0 la maison ou en d\u00e9placement.'
      },
      caregivers: {
        title: 'Aidants et membres de la famille',
        description: 'Prot\u00e9g\u00e9, organis\u00e9 et plus facile \u00e0 identifier \u2014 pour le m\u00e9dicament que vous aidez \u00e0 transporter au quotidien.'
      },
      healthcare: {
        title: 'Professionnels de la sant\u00e9',
        description: 'Recommandez une conception guid\u00e9e par l\u2019usage r\u00e9el aux patients qui doivent transporter des m\u00e9dicaments d\u2019urgence quotidiennement.'
      },
      responders: {
        title: 'Premiers r\u00e9pondants',
        description: 'Un \u00e9tui con\u00e7u pour faciliter l\u2019identification et l\u2019acc\u00e8s \u00e0 la trousse.'
      },
      partnership: '\u00c9coles, cliniques ou organisations?',
      learnMore: 'En savoir plus sur les partenariats'
    },
    origin: {
      badge: 'Notre origine',
      title: 'N\u00e9 d\u2019une vraie urgence',
      description: "MyGuardPod a commenc\u00e9 avec un probl\u00e8me simple.\n\nLes contenants de m\u00e9dicaments d\u2019urgence sont souvent transport\u00e9s libres dans des sacs \u00e0 dos, des sacs \u00e0 main ou des poches, o\u00f9 ils peuvent \u00eatre boulevers\u00e9s, \u00e9cras\u00e9s ou \u00e9gar\u00e9s.\n\nNous concevons les \u00e9tuis MyGuardPod pour ajouter une barri\u00e8re physique pendant le transport quotidien et pour garder les contenants de m\u00e9dicaments compatibles organis\u00e9s, visibles et plus faciles \u00e0 identifier. Chaque \u00e9tui est imprim\u00e9 en 3D \u00e0 la main \u00e0 Baie-Comeau, au Qu\u00e9bec \u2014 con\u00e7u pour un format de m\u00e9dicament sp\u00e9cifique et utilis\u00e9 par des patients et aidants \u00e0 travers le monde.",
      cta: 'Lire notre histoire compl\u00e8te'
    },
    testimonial: {
      quote: '\u00abCet \u00e9tui est exactement ce dont j\u2019avais besoin. Construction solide, facile \u00e0 rep\u00e9rer dans mon sac, et je peux acc\u00e9der \u00e0 mon m\u00e9dicament en quelques secondes. La tranquillit\u00e9 d\u2019esprit n\u2019a pas de prix.\u00bb',
      author: 'Parent d\u2019un enfant \u00e9pileptique \u2014 \u00c9tats-Unis'
    },
    faq: {
      title: 'Questions fr\u00e9quentes',
      sections: [
        {
          title: 'Questions g\u00e9n\u00e9rales',
          items: [
            {
              question: 'Qu\u2019est-ce que MyGuardPod?',
              answer: 'MyGuardPod con\u00e7oit des \u00e9tuis protecteurs de transport pour des m\u00e9dicaments d\u2019urgence essentiels comme le Valtoco, Nayzilam, les kits d\u2019hydrocortisone injectable d\u2019urgence, l\u2019EpiPen, la Naloxone et la nitroglyc\u00e9rine. Les \u00e9tuis sont con\u00e7us comme accessoires protecteurs de transport uniquement.'
            },
            {
              question: 'Pourquoi ne pas simplement utiliser une pochette ordinaire?',
              answer: 'Les m\u00e9dicaments d\u2019urgence sont fragiles et souvent co\u00fbteux. Une pochette ordinaire offre peu de protection contre les impacts, l\u2019\u00e9crasement ou l\u2019exposition environnementale. Les \u00e9tuis MyGuardPod sont con\u00e7us pour prot\u00e9ger les m\u00e9dicaments dans des situations r\u00e9elles comme les sacs \u00e0 dos, les poches, les sacs de voyage ou les activit\u00e9s ext\u00e9rieures.'
            },
            {
              question: 'O\u00f9 sont fabriqu\u00e9s les produits MyGuardPod?',
              answer: 'Tous les \u00e9tuis MyGuardPod sont con\u00e7us et produits au Qu\u00e9bec, Canada. Chaque produit est fabriqu\u00e9 en petits lots avec des mat\u00e9riaux imprim\u00e9s en 3D haute r\u00e9sistance et assembl\u00e9 \u00e0 la main.'
            }
          ]
        },
        {
          title: 'Compatibilit\u00e9 des produits',
          items: [
            {
              question: 'Comment savoir si un \u00e9tui convient \u00e0 mon m\u00e9dicament?',
              answer: 'Chaque \u00e9tui MyGuardPod est con\u00e7u pour un m\u00e9dicament ou un kit d\u2019urgence sp\u00e9cifique. Les pages produits listent clairement les m\u00e9dicaments compatibles pour assurer un ajustement parfait.'
            },
            {
              question: 'Les \u00e9tuis peuvent-ils contenir d\u2019autres m\u00e9dicaments?',
              answer: 'Les \u00e9tuis sont optimis\u00e9s pour des m\u00e9dicaments sp\u00e9cifiques. Certains peuvent accueillir des articles similaires, mais pour la meilleure protection, nous recommandons d\u2019utiliser l\u2019\u00e9tui con\u00e7u pour votre m\u00e9dicament.'
            }
          ]
        },
        {
          title: 'Protection et utilisation',
          items: [
            {
              question: 'Les \u00e9tuis prot\u00e8gent-ils contre les chutes?',
              answer: 'Les \u00e9tuis MyGuardPod ajoutent une barri\u00e8re physique contre certains chocs, certaines pressions, les \u00e9gratignures et le mouvement normal \u00e0 l\u2019int\u00e9rieur d\u2019un sac. Ils ne rendent pas le m\u00e9dicament ou son contenant incassable et ne garantissent pas son int\u00e9grit\u00e9 apr\u00e8s une chute ou un impact important. Apr\u00e8s un incident, inspectez toujours le m\u00e9dicament, son contenant et son emballage conform\u00e9ment aux instructions officielles.'
            },
            {
              question: 'Les \u00e9tuis contr\u00f4lent-ils la temp\u00e9rature?',
              answer: 'Non. Les \u00e9tuis MyGuardPod standards ne chauffent, ne refroidissent et ne contr\u00f4lent pas activement la temp\u00e9rature. Ils ne prolongent pas la dur\u00e9e de conservation et ne garantissent ni la stabilit\u00e9 ni l\u2019efficacit\u00e9 du m\u00e9dicament. Respectez les consignes du fabricant et consultez un pharmacien ou un professionnel de la sant\u00e9 au besoin.'
            },
            {
              question: 'Les \u00e9tuis sont-ils \u00e9tanches?',
              answer: 'Non. \u00c0 moins qu\u2019un produit pr\u00e9cis poss\u00e8de une classification test\u00e9e et document\u00e9e s\u00e9par\u00e9ment, les \u00e9tuis MyGuardPod ne sont ni \u00e9tanches ni submersibles. Gardez l\u2019\u00e9tui au sec et inspectez le m\u00e9dicament apr\u00e8s toute exposition \u00e0 l\u2019humidit\u00e9.'
            },
            {
              question: 'Comment confirmer la compatibilit\u00e9 avec mon m\u00e9dicament?',
              answer: 'V\u00e9rifiez le nom du m\u00e9dicament, la dose, le fabricant, le type d\u2019emballage et les dimensions indiqu\u00e9es dans chaque fiche de produit. Une apparence similaire ne garantit pas la compatibilit\u00e9. Les fabricants peuvent aussi modifier leur emballage au fil du temps. En cas de doute, contactez-nous avant l\u2019achat.'
            },
            {
              question: 'Les \u00e9tuis sont-ils st\u00e9riles ou \u00e0 l\u2019\u00e9preuve des enfants?',
              answer: 'Non. Les \u00e9tuis MyGuardPod ne sont ni st\u00e9riles ni \u00e0 l\u2019\u00e9preuve des enfants. Ce sont uniquement des accessoires protecteurs de transport. Rangez votre m\u00e9dicament en s\u00e9curit\u00e9, hors de port\u00e9e des enfants, et respectez les consignes de conservation fournies avec votre m\u00e9dicament.'
            }
          ]
        },
        {
          title: 'Commandes et livraison',
          items: [
            {
              question: 'D\u2019o\u00f9 exp\u00e9diez-vous?',
              answer: 'Les commandes sont exp\u00e9di\u00e9es depuis le Qu\u00e9bec, Canada.'
            },
            {
              question: 'Livrez-vous partout dans le monde?',
              answer: 'Oui. MyGuardPod livre dans de nombreux pays \u00e0 travers le monde via Etsy.'
            },
            {
              question: 'Combien de temps prend la livraison?',
              answer: 'Les d\u00e9lais de livraison varient selon la destination, mais la plupart des commandes sont pr\u00e9par\u00e9es et exp\u00e9di\u00e9es en quelques jours.'
            },
            {
              question: 'O\u00f9 puis-je acheter les produits MyGuardPod?',
              answer: 'Les produits sont actuellement disponibles sur la boutique Etsy officielle de MyGuardPod.'
            },
            {
              question: 'Je suis aux \u00c9tats-Unis. Devrai-je payer un tarif douanier additionnel \u00e0 la livraison?',
              answer: 'Non. Nos prix Etsy am\u00e9ricains ont d\u00e9j\u00e0 \u00e9t\u00e9 ajust\u00e9s pour tenir compte du tarif d\u2019importation am\u00e9ricain additionnel actuellement en vigueur sur les produits canadiens vis\u00e9s. Aucun tarif additionnel de 50 % ne vous sera demand\u00e9 \u00e0 la livraison. Consultez la section \u00ab Information importante pour les clients am\u00e9ricains \u00bb ci-dessous pour plus de d\u00e9tails.'
            }
          ]
        },
        {
          title: 'S\u00e9curit\u00e9 et avertissement',
          items: [
            {
              question: 'Quelle est la fonction pr\u00e9vue d\u2019un \u00e9tui MyGuardPod?',
              answer: 'Les \u00e9tuis MyGuardPod sont commercialis\u00e9s comme accessoires protecteurs de transport. Ils n\u2019administrent, ne diagnostiquent, ne traitent, ne dosent, ne surveillent et ne modifient aucun m\u00e9dicament. MyGuardPod ne revendique aucune approbation, autorisation ou certification comme dispositif m\u00e9dical.'
            },
            {
              question: 'Puis-je demander un \u00e9tui pour un m\u00e9dicament sp\u00e9cifique?',
              answer: 'Oui. Nous d\u00e9veloppons r\u00e9guli\u00e8rement de nouveaux designs bas\u00e9s sur les besoins des clients. Si vous avez besoin d\u2019un \u00e9tui pour un m\u00e9dicament sp\u00e9cifique, vous pouvez nous contacter pour demander un nouveau design.'
            }
          ]
        }
      ]
    },
    contact: {
      title: 'Contactez-nous',
      subtitle: 'Des questions sur nos produits ou int\u00e9ress\u00e9 par des commandes en gros? Nous serions ravis de vous entendre.',
      form: {
        name: 'Votre nom',
        email: 'Adresse courriel',
        subject: 'Sujet',
        message: 'Votre message',
        send: 'Envoyer le message',
        sending: 'Envoi en cours...',
        success: 'Message envoy\u00e9 avec succ\u00e8s!',
        error: '\u00c9chec de l\u2019envoi. Veuillez r\u00e9essayer.'
      },
      info: {
        location: 'Baie-Comeau, Qu\u00e9bec, Canada',
        email: 'contact@myguardpod.com'
      }
    },
    cta: {
      title: 'Pr\u00eat \u00e0 transporter, organiser et identifier votre m\u00e9dicament?',
      description: 'Parcourez la collection compl\u00e8te sur Etsy. Chaque \u00e9tui est fabriqu\u00e9 sur commande au Qu\u00e9bec. Le d\u00e9lai de traitement actuel est indiqu\u00e9 dans la fiche Etsy correspondante.',
      button: 'Acheter sur Etsy'
    },
    footer: {
      tagline: 'Prot\u00e9gez Ce Qui Vous Prot\u00e8ge',
      description: '\u00c9tuis de transport protecteurs con\u00e7us pour organiser, identifier et ajouter une protection physique aux contenants de m\u00e9dicaments d\u2019urgence et au mat\u00e9riel compatibles. Fabriqu\u00e9s sur commande au Qu\u00e9bec, Canada.',
      quickLinks: 'Liens rapides',
      legal: 'L\u00e9gal',
      privacy: 'Politique de confidentialit\u00e9',
      terms: 'Conditions d\u2019utilisation',
      copyright: '\u00a9 2026 MyGuardPod. Tous droits r\u00e9serv\u00e9s.',
      madeIn: 'Con\u00e7u et fabriqu\u00e9 \u00e0 Baie-Comeau, Qu\u00e9bec'
    }
  }
};
